/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useContext } from 'react';
import { links, tierBadges, pbSpendSummary } from '../../../constants';
import { IProXtraSpend, IUser } from '../../../models/b2b';
import { LegacyTiersContext } from '../../../contexts';
import { getImageURL, getHostURL } from '../../../utils/GeneralUtil';
import { SpendSummaryTooltip } from '../../shared/tooltips/SpendSummaryTooltip';
import { getUserRole } from '../../Utils';
import { stashLinkEvent } from '../../../utils/analytics';
import { InfoPopover } from '../shared/popover';

interface ProXtraSpendProps {
  proXtraSpend: IProXtraSpend;
  userProfile: IUser;
  isMobile: boolean;
}

type mBG = { MEMBER: string; ELITE: string; VIP: string };

export const PricingSummary = ({
  ...props
}: ProXtraSpendProps): JSX.Element => {
  const { membershipLevel } = useContext(LegacyTiersContext);
  const userRole = getUserRole();
  const membership = tierBadges[membershipLevel];

  const { isMobile, proXtraSpend } = props;
  // eslint-disable-next-line radix
  const savingDesk = parseInt(proXtraSpend?.totalCurrentYearSavings) > 0;
  const userRoleClass = userRole === 'ADMIN' ? 'sui-cursor-pointer' : '';
  const membershipBG = {
    MEMBER: '#f96302',
    ELITE: '#C95627',
    VIP: '#6C6D6D',
  }[`${membershipLevel || 'MEMBER'}` as keyof mBG];

  const RenderToolTipMessage = (): JSX.Element => (
    <div>
      <p>{pbSpendSummary.spendIncludesLoyalty}&nbsp;</p>
      <br />
      <p>{pbSpendSummary.savingIncludes}</p>
      <br />
      <p>
        {pbSpendSummary.fullDetails}&nbsp;
        <a
          className="u__default-link"
          target="_blank"
          href={links.faqTermsAndConditions}
          rel="noreferrer"
        >
          {links.termsAndConditionsTitle}
        </a>
      </p>
    </div>
  );

  const RenderTooltip = (): JSX.Element => (
    <InfoPopover iconSize="small">
      <RenderToolTipMessage />
    </InfoPopover>
  );

  const RenderSpendSaving = (): JSX.Element => (
    <div
      data-testid="render-spend-saving"
      className="sui-flex sui-flex-row sui-justify-end sui-align-center sui-flex-nowrap sui-w-auto"
    >
      <div
        data-testid="pricing-summary__proSpend"
        className="sui-relative sui-text-right sui-pr-[2.6rem] sui-whitespace-nowrap sui-self-center"
      >
        {pbSpendSummary.proXtraLoyaltySpend}:
      </div>
      <div
        data-testid="pricing-summary__spendValue"
        className="sui-text-right sui-pr-[22.5px] sui-self-center"
      >
        <b>${proXtraSpend?.totalCurrentYearSpend || '0'}</b>
      </div>
      {savingDesk && (
        <div data-testid="desktop-savings" className="hidden sm:sui-flex">
          <div
            data-testid="pricing-summary__saving"
            className="sui-pl-0 sm:sui-pr-3 md:sui-pr-[2.6rem] lg:sui-pr-[2.6rem] sui-self-center"
          >
            {pbSpendSummary.saving}:
          </div>
          <div
            data-testid="pricing-summary__savingValue"
            className="sui-text-success sui-pr-5 sui-self-center"
          >
            <b>${proXtraSpend?.totalCurrentYearSavings || 0}</b>
          </div>
        </div>
      )}
      <div
        data-testid="pricing-summary__tooltip-desktop"
        className="pricing-summary__tooltip-desktop sui-pb-[3px] sui-pr-4 sui-flex sui-min-w-10"
      >
        <SpendSummaryTooltip />
      </div>
    </div>
  );

  const RenderFirstName = (): JSX.Element => {
    const first = props.userProfile?.name?.firstName;
    const last = props.userProfile?.name?.lastName;
    const firstName =
      first && `${first[0].toUpperCase()}${first.slice(1).toLowerCase()}`;
    const lastName =
      last && `${last[0].toUpperCase()}${last.slice(1).toLowerCase()}`;
    return (
      <div
        data-testid="pricing-summary__singleLine"
        className="sui-overflow-hidden sui-text-ellipsis sui-whitespace-nowrap sui-break-words sui-text-left sui-block lg:sui-ml-5 xs:sui-ml-0"
      >
        <span
          data-testid="pricing-summary__pro-xtra-summary"
          className="sui-hidden 2xl:sui-inline-flex"
        >
          Pro Xtra Summary {firstName === undefined || '' ? '' : 'for'}&nbsp;
        </span>
        {firstName}&nbsp;{lastName}
      </div>
    );
  };

  const RenderTierBadge = (): JSX.Element => {
    return (
      <div
        data-testid="pricing-summary__tier-section"
        className="sui-min-w-44 sui-mb-1 sm:sui-mb-0 sui-flex sui-flex-row sui-flex-nowrap sui-relative sui-mb-1 xs:sui-mb-0"
      >
        <button
          type="button"
          data-testid="pricing-summary__badge"
          className={`sui-w-[90%] sui-flex sui-flex-row sui-align-center sui-h-[36px] sm:absolute sm:sui-w-[130px] sui-ml-[-1.2rem] sm:sui-ml-0 ${userRoleClass}`}
          style={{ background: membershipBG }}
          onClick={() => {
            if (userRole === 'ADMIN') {
              window.location.href = getHostURL(
                'myaccount/proxtraprogramdetails',
              );
              stashLinkEvent('pricing benefits: member tier level badge click');
            }
          }}
        >
          {isMobile && (
            <img
              className="sui-ml-4 sui-h-[14px] sui-self-center"
              data-testid="pricing-summary__proXtra"
              src={getImageURL('ProTiers/Pro-Xtra-logo-text.svg')}
              alt="Pro-Xtra"
              height="14px"
              width="auto"
            />
          )}
          <div
            data-testid="pricing-summary__badge-text"
            className="sui-font-display sui-self-center sui-text-lg sui-text-inverse sui-ml-2 md:sui-ml-4"
          >
            {membership?.label}
          </div>
          <img
            className="sui-ml-2 sui-self-center"
            data-testid="pricing-summary__tier-caret"
            src={getImageURL('ProTiers/caret-white.svg')}
            alt="caret"
            height="auto"
            width="auto"
          />
        </button>
        <img
          data-testid="pricing-summary__tier-icon"
          className="sm:sui-absolute sm:sui-left-32 sui-h-[3.7rem]"
          src={membership?.icon}
          alt="x-badge"
          height="auto"
          width="auto"
        />
      </div>
    );
  };

  const RenderMobilePricingSummary = (): JSX.Element => {
    return (
      <div
        data-testid="pricing-summary__box"
        className="sui-h-auto sui-border-1 sui-border-solid sui-border-primary lg:sui-rounded-md"
      >
        <div
          data-testid="pricing-summary__insideBox"
          className="sui-font-regular sui-h-full sui-p-3 md:sui-py-3 sui-font-regular sui-text-base md:sui-text-lg"
        >
          <RenderTierBadge />
          <div>
            <div
              data-testid="pricing-summary__spendBody"
              className="sui-flex sui-justify-between sui-flex-nowrap -sui-mb-2 sui-items-center"
            >
              <div
                data-testid="pricing-summary__FirstName"
                className="sui-max-w-[85%] sui-font-w-bold"
              >
                <RenderFirstName />
              </div>
              <div
                data-testid="pricing-summary__tooltip-mobile"
                className="pricing-summary__tooltip-mobile -sui-mr-2"
              >
                <RenderTooltip />
              </div>
            </div>
            <div
              data-testid="pricing-summary__rowSpend"
              className="sui-mt-2 sui-flex sui-justify-between sui-flex-nowrap"
            >
              <p>{pbSpendSummary.proXtraLoyaltySpend}</p>
              <b>${proXtraSpend?.totalCurrentYearSpend || '0'}</b>
            </div>
            <div
              data-testid="pricing-summary__rowSavings"
              className="sui-mt-2 sui-flex sui-justify-between sui-flex-nowrap"
            >
              <p>Total Savings</p>
              <b className="sui-text-success sui-text-right sui-pr-0 lg:sui-pr-5">
                ${proXtraSpend?.totalCurrentYearSavings || 0}
              </b>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const RenderDesktopPricingSummary = (): JSX.Element => {
    return (
      <div
        data-testid="pricing-summary__boxDesktop"
        className="sui-border-1 sui-border-solid sui-border-primary sui-h-auto sui-rounded-none lg:sui-rounded-md"
      >
        <div
          data-testid="pricing-summary__insideBoxDesktop"
          className="sui-font-regular sui-flex sui-justify-between sui-align-center sui-flex-nowrap sui-flex-row sui-h-full sui-py-3 sui-font-regular sui-text-base md:sui-text-lg"
        >
          <div
            data-testid="pricing-summary__leftBox"
            className="sui-flex sui-justify-start sui-align-center sui-flex-nowrap sui-flex-row sui-overflow-hidden sui-whitespace-nowrap sui-min-w-0 sui-w-auto sui-h-9"
          >
            <div>
              <RenderTierBadge />
            </div>
            <div className="psui-min-w-0 sui-self-center">
              <RenderFirstName />
            </div>
          </div>
          <div
            data-testid="pricing-summary__rightBox"
            className="sui-flex sui-justify-end sui-flex-nowrap sui-whitespace-nowrap sui-self-center sui-w-auto lg:sui-pl-6"
          >
            <RenderSpendSaving />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="sui-mb-2 md:sui-mb-10">
      {isMobile ? (
        <RenderMobilePricingSummary />
      ) : (
        <RenderDesktopPricingSummary />
      )}
    </div>
  );
};
